import React, { ReactElement, useCallback, useEffect, useMemo, useState, useContext } from 'react'
import styled from 'styled-components'
import LiveLoading from 'live/components/ui/Loading'
import i18n from 'live/services/i18n'
import { StaticDataTypeI18nKey, STATIC_DATA_STATES } from '../chart-components'
import { CustomModalsProps, GenericWarning, ModalTabSections } from '../modal-components'
import { ModalHeader, ModalBody, ModalFooter, ModalSideBody, InformationIcon } from '../modal-components-style'
import RestrictedPerformanceModal from '../activity/modalComponents/RestrictedPerformanceModal'
import {
    validateActivityFields,
    validateRestrictedPipeFields,
    validateRestrictedConnectionFields,
    validateRestrictedRateOfPenetrationFields
} from './activityValidations'
import {
    RawActivityEvent,
    RawRunEvent,
    RestrictedConnection,
    RestrictedPipeMovement,
    RestrictedRateOfPenetration,
    SelectOptions
} from '../../utils/type'
import {
    createSingleEntity,
    fetchEntityStartTimestamp,
    fetchSingleEntity,
    fetchRigIdFromName,
    RUN_API,
    TUBULAR_API,
    updateSingleEntity
} from '../../api/api'
import { emptyRawRunEvent } from '../run/types'
import { getEmptyErrors, emptyRawActivityEvent, Errors } from './types'
import { DashboardContext } from 'live/context/dashboard'

import { createTubularsByDepth } from '../../api/activityApi'
import { updateRunExtendLastTubular } from '../../api/runApi'
import { StaticDataContext, useStaticDataWidget } from '../../context/StaticDataContext'
import ActivityTab from './ActivityTab'
import ActivityFooter from './modalComponents/Footer'
import _ from 'lodash'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'

export const NOT_APPLICABLE = 'Not applicable'

const errorMessage = i18n('Please correct the highlighted fields')

interface ActivityModalProps extends Omit<CustomModalsProps, 'closeModal'> {
    event: RawActivityEvent
    setIsModalDoubleSize: (isDouble: boolean) => void
    isModalDoubleSize: boolean
    closeModal: (activityData?: any, action?: string) => void
}
function getEmptyDepthEvent(rawTubularList: RawActivityEvent[], emptyEvent: RawActivityEvent): RawActivityEvent {
    if (!rawTubularList.length) return emptyEvent
    const lastTubular = rawTubularList[rawTubularList.length - 1]

    return { ...emptyEvent, start: lastTubular.end }
}
export const ActivityModal = ({
    title = StaticDataTypeI18nKey.ACTIVITY,
    closeModal,
    setIsModalDoubleSize,
    isModalDoubleSize,
    event
}: ActivityModalProps): ReactElement => {
    const { reloadData } = useStaticDataWidget()
    const emptyActivityEvent = { ...emptyRawActivityEvent, ...event }
    const [loading, setLoading] = useState<boolean>(true)
    const [rawActivityEvent, setRawActivityEvent] = useState<RawActivityEvent>(event?.id ? event : emptyActivityEvent)
    const [rawTubularList, setRawTubularList] = useState<RawActivityEvent[]>([])
    const [tubularIndex, setTubularIndex] = useState<number>(0)
    const [runEvent, setRunEvent] = useState<RawRunEvent>(emptyRawRunEvent)
    const [selectedTubularType, setSelectedTubularType] = useState<SelectOptions | null>(null)
    const [assetFilterId, setAssetFilterId] = useState<string | undefined>(undefined)
    const [isAddRestrictedPipesButtonDisabled, setIsAddRestrictedPipesButtonDisabled] = useState<boolean>(false)
    const [isAddRestrictedConnectionButtonDisabled, setIsAddRestrictedConnectionButtonDisabled] = useState<boolean>(
        false
    )
    const [isAddRestrictedROPButtonDisabled, setIsAddRestrictedROPButtonDisabled] = useState<boolean>(false)
    const [showErrorFinalMDInput, setShowErrorFinalMDInput] = useState<boolean>(false)
    const dashboardContext = useContext(DashboardContext)
    const { liveTheme } = useContext(StaticDataContext)

    const isAddRestrictedButtonDisabled =
        isAddRestrictedPipesButtonDisabled ||
        isAddRestrictedConnectionButtonDisabled ||
        isAddRestrictedROPButtonDisabled

    const isSingleEvent = useMemo(() => runEvent?.tubularsIntervalPerTime || rawActivityEvent?.id, [
        runEvent?.tubularsIntervalPerTime,
        rawActivityEvent?.id
    ])

    const activityEvent: RawActivityEvent = useMemo(
        () =>
            isSingleEvent
                ? rawActivityEvent
                : rawTubularList[tubularIndex] || getEmptyDepthEvent(rawTubularList, { ...emptyActivityEvent }),
        [isSingleEvent, rawActivityEvent, rawTubularList, tubularIndex, emptyActivityEvent]
    )

    const tubularsWithPermanentInstallation = rawTubularList.filter(t => t.permanentTubularInstallation === true)
    const isPreviousCheckboxTrue =
        !runEvent?.tubularsIntervalPerTime && tubularIndex <= tubularsWithPermanentInstallation.length

    const setMDFinalOfPreviousTubular =
        activityEvent?.state !== STATIC_DATA_STATES.FINISHED
            ? rawTubularList[tubularIndex - 1]?.finalMD?.value
            : activityEvent.startMD?.value

    const hasRestrictedPerformance =
        activityEvent &&
        (activityEvent?.restrictedPipeMovements?.length ||
            activityEvent?.restrictedConnections?.length ||
            activityEvent?.restrictedRops?.length)

    const [hasChanged, setHasChanged] = useState<boolean>(false)

    const [isDeleteConfirmationModalOpen, setIsDeleteConfirmationModalOpen] = useState<boolean>(false)
    const closeDeleteConfirmationModal = (): void => setIsDeleteConfirmationModalOpen(false)

    const scrollToTop = () => {
        const modal = document.getElementById('modal-body')

        modal?.scrollTo({
            top: 0,
            behavior: 'smooth'
        })
    }

    const updateTubularIndex = useCallback((number: number) => {
        setTubularIndex(prevstate => prevstate + number)
    }, [])

    const updateRunExtendLastTubularWithDebounce: (updatedRun: RawRunEvent) => void = useCallback(
        _.debounce((updatedRun: RawRunEvent) => updateRunExtendLastTubular(updatedRun), 600),
        []
    )

    const handleExtendLastTubularClick = useCallback(() => {
        if (!runEvent) return
        setRunEvent(run => {
            const newRun = { ...run, extendLastTubular: !run?.extendLastTubular }
            updateRunExtendLastTubularWithDebounce(newRun)

            return newRun
        })
    }, [runEvent])

    const updateActivityEvent = useCallback((newEvent: RawActivityEvent) => {
        setRawActivityEvent(newEvent)
    }, [])

    const postActivity = useCallback(
        (rawActivity: RawActivityEvent[]): any => {
            if (!rawActivity || rawActivity.length === 0) return null

            let activityData: any = rawActivity

            if (!runEvent?.tubularsIntervalPerTime) {
                activityData = activityData.map(event => ({
                    ...event,
                    start: null,
                    end: null,
                    state: null
                }))
            }

            return (activityData.length > 1
                ? createTubularsByDepth(activityData)
                : activityData[0].id
                ? updateSingleEntity(TUBULAR_API, activityData[0])
                : createSingleEntity(TUBULAR_API, activityData[0])
            ).then(() => {
                closeModal()
                reloadData()
            })
        },
        [runEvent?.tubularsIntervalPerTime]
    )

    const updateTubularList = useCallback(
        (event: RawActivityEvent, index: number, previousButtonClicked?: boolean) => {
            const newTubularList = [...rawTubularList]
            newTubularList[index] = event

            if (previousButtonClicked) {
                newTubularList.pop()

                return setRawTubularList(newTubularList)
            }

            setRawTubularList(newTubularList)
        },
        [rawTubularList]
    )
    const [errors, setErrors] = useState<Errors>(getEmptyErrors())

    const updateEvent = useCallback(
        newEvent => {
            if (isSingleEvent) {
                updateActivityEvent({ ...activityEvent, ...(newEvent ?? {}) })
            } else {
                updateTubularList({ ...activityEvent, ...(newEvent ?? {}) }, tubularIndex)
            }
        },
        [isSingleEvent, activityEvent, tubularIndex, updateActivityEvent, updateTubularList]
    )

    useEffect(() => {
        if (event.state === STATIC_DATA_STATES.EMPTY) {
            const hasPipeMovementsFilled = activityEvent?.restrictedPipeMovements?.some(
                restricted => restricted?.finalMD?.value || restricted?.end
            )
            const hasRestrictedConectionsFilled = activityEvent?.restrictedConnections?.some(
                restricted => restricted?.finalMD?.value || restricted?.end
            )
            const hasRestrictedRopsFilled = activityEvent?.restrictedRops?.some(
                restricted => restricted?.finalMD?.value || restricted?.end
            )

            const emptyEvents = { ...activityEvent }

            if (!hasPipeMovementsFilled) {
                emptyEvents.restrictedPipeMovements = []
            }
            if (!hasRestrictedConectionsFilled) {
                emptyEvents.restrictedConnections = []
            }
            if (!hasRestrictedRopsFilled) {
                emptyEvents.restrictedRops = []
            }

            setIsAddRestrictedPipesButtonDisabled(false)
            setIsAddRestrictedConnectionButtonDisabled(false)
            setIsAddRestrictedROPButtonDisabled(false)

            updateEvent(emptyEvents)
        }
    }, [selectedTubularType])

    useEffect(() => {
        fetchSingleEntity(RUN_API, event.run_id).then(runData => {
            setRunEvent(runData)
            if (!event.id && runData.tubularsIntervalPerTime) {
                fetchEntityStartTimestamp(event.run_id, TUBULAR_API, RUN_API).then(activityStart => {
                    if (activityStart) {
                        setRawActivityEvent(prevstate => ({ ...prevstate, start: activityStart }))
                    }
                    setLoading(false)
                })
            } else {
                const newActivityEvent = runData?.tubulars?.find(tubular => tubular.id == activityEvent.id)
                if (newActivityEvent && !runData?.tubularsIntervalPerTime) {
                    setRawActivityEvent(prevstate => ({
                        ...prevstate,
                        state: STATIC_DATA_STATES.FINISHED,
                        finalMD: newActivityEvent?.finalMD
                    }))
                }
                setLoading(false)
            }
        })
        const rigName = dashboardContext?.state?.currentFilterValues?.asset_name_event_type[0]
        rigName && fetchRigIdFromName(rigName).then(setAssetFilterId)
    }, [])

    const validateFields = useCallback(
        (
            event: RawActivityEvent
        ): {
            pass: boolean
            errors: Errors
        } => {
            const errors: Errors = getEmptyErrors()

            const isTubularFinalMDGreaterThanRunFinalMD =
                event.finalMD?.value && runEvent.finalMD?.value && event.finalMD?.value > runEvent.finalMD?.value

            const { pass: passTubular, errors: tubularErrors } = validateActivityFields(event, runEvent)
            if (!passTubular || isTubularFinalMDGreaterThanRunFinalMD) {
                errors.messages.activity = errorMessage
                errors.activity = tubularErrors
            }
            setShowErrorFinalMDInput(isTubularFinalMDGreaterThanRunFinalMD)
            setErrors(errors)

            return { pass: !isTubularFinalMDGreaterThanRunFinalMD && passTubular, errors: errors }
        },
        [runEvent]
    )

    const openDeleteConfirmationModal = (): void => {
        setIsDeleteConfirmationModalOpen(true)
    }

    const handleDelete = () => {
        updateActivityEvent({
            ...activityEvent,
            restrictedPipeMovements: [],
            restrictedConnections: [],
            restrictedRops: []
        })
        closeDeleteConfirmationModal()
    }

    return (
        <>
            {loading ? (
                <LiveLoading />
            ) : (
                <ActivityModalContainer>
                    {' '}
                    <ModalHeader>{i18n(title).toUpperCase()}</ModalHeader>
                    <>
                        <ScrollModalContainer>
                            <ModalBody
                                id="modal-body"
                                style={{
                                    width: '344px',
                                    paddingBottom: '22px'
                                }}
                                liveTheme={liveTheme}
                            >
                                <ModalTabSections
                                    selected
                                    content={
                                        <div onMouseDown={e => e.stopPropagation()}>
                                            <ActivityTab
                                                errors={errors.activity}
                                                event={activityEvent}
                                                key={tubularIndex}
                                                updateActivityEvent={({
                                                    restrictedPipeMovements,
                                                    restrictedConnections,
                                                    restrictedRops,
                                                    ...activityData
                                                }) => updateEvent(activityData)}
                                                runEvent={runEvent}
                                                onExtendLastTubularClick={handleExtendLastTubularClick}
                                                setSelectedTubularType={setSelectedTubularType}
                                                isPreviousCheckboxTrue={isPreviousCheckboxTrue}
                                                setMDFinalOfPreviousTubular={setMDFinalOfPreviousTubular}
                                                isFirstTubularByDepth={tubularIndex === 0}
                                                showErrorFinalMDInput={showErrorFinalMDInput}
                                                handleDelete={handleDelete}
                                                setHasChanged={setHasChanged}
                                                clearErrors={() => setErrors(getEmptyErrors())}
                                            ></ActivityTab>
                                        </div>
                                    }
                                />
                                <GenericWarning message={errors?.messages?.activity} />
                            </ModalBody>

                            {isModalDoubleSize && (
                                <ModalSideBody liveTheme={liveTheme}>
                                    <RestrictedPerformanceModal
                                        restrictedPipes={activityEvent?.restrictedPipeMovements}
                                        updateRestrictedPipes={(restrictedPipeMovements: RestrictedPipeMovement[]) => {
                                            updateEvent({ restrictedPipeMovements })
                                        }}
                                        validateRestrictedPipe={(restrictedPipe: RestrictedPipeMovement) =>
                                            validateRestrictedPipeFields(restrictedPipe, activityEvent, runEvent)
                                        }
                                        setIsAddRestrictedPipesButtonDisabled={setIsAddRestrictedPipesButtonDisabled}
                                        restrictedConnections={activityEvent?.restrictedConnections}
                                        updateRestrictedConnections={(
                                            restrictedConnections: RestrictedConnection[]
                                        ) => {
                                            updateEvent({ restrictedConnections })
                                        }}
                                        validateRestrictedConnection={(restrictedConnection: RestrictedConnection) =>
                                            validateRestrictedConnectionFields(
                                                restrictedConnection,
                                                activityEvent,
                                                runEvent
                                            )
                                        }
                                        setIsAddRestrictedConnectionButtonDisabled={
                                            setIsAddRestrictedConnectionButtonDisabled
                                        }
                                        restrictedRateOfPenetrations={activityEvent?.restrictedRops}
                                        updateRestrictedRateOfPenetrations={(
                                            restrictedRops: RestrictedRateOfPenetration[]
                                        ) => {
                                            updateEvent({ restrictedRops })
                                        }}
                                        validateRestrictedRop={(
                                            restrictedRateOfPenetration: RestrictedRateOfPenetration
                                        ) =>
                                            validateRestrictedRateOfPenetrationFields(
                                                restrictedRateOfPenetration,
                                                activityEvent,
                                                runEvent
                                            )
                                        }
                                        setIsAddRestrictedROPButtonDisabled={setIsAddRestrictedROPButtonDisabled}
                                        tubularsIntervalPerTime={runEvent?.tubularsIntervalPerTime}
                                        tubularType={selectedTubularType}
                                        assetFilterId={assetFilterId}
                                        showForm={activityEvent?.state === STATIC_DATA_STATES.EMPTY}
                                        errors={errors}
                                        setErrors={setErrors}
                                        event={activityEvent}
                                    />
                                </ModalSideBody>
                            )}
                        </ScrollModalContainer>
                    </>
                    <ModalFooter
                        liveTheme={liveTheme}
                        style={{ display: 'block', height: 'auto', padding: '0', border: 'none' }}
                    >
                        <RestrictedPerformanceContainer
                            isDisabled={!activityEvent.tubularType || isAddRestrictedButtonDisabled}
                            onClick={() => {
                                activityEvent.tubularType && setIsModalDoubleSize(!isModalDoubleSize)
                            }}
                            liveTheme={liveTheme}
                        >
                            <RestrictedPerformanceContainerSection>
                                <div>
                                    <i
                                        className={`far fa-${hasRestrictedPerformance ? 'check' : 'minus'}-circle`}
                                        style={
                                            hasRestrictedPerformance
                                                ? { paddingRight: '6px', color: '#01C292' }
                                                : { paddingRight: '6px', color: '#868695' }
                                        }
                                    ></i>
                                    {hasRestrictedPerformance
                                        ? i18n('Restricted Performance')
                                        : i18n('No Restricted Performance')}
                                    {!activityEvent.tubularType ? (
                                        <OverlayTrigger
                                            placement="top"
                                            overlay={
                                                <Tooltip style={{ zIndex: 9999 }} id={''}>
                                                    {i18n('Tubular type option is required for goals to appear')}
                                                </Tooltip>
                                            }
                                        >
                                            <InformationIcon className="far fa-info-circle" />
                                        </OverlayTrigger>
                                    ) : (
                                        isAddRestrictedButtonDisabled && (
                                            <OverlayTrigger
                                                placement="top"
                                                overlay={
                                                    <Tooltip style={{ zIndex: 9999 }} id={''}>
                                                        {i18n(
                                                            'All Restricted Performance fields need to be filled in to be able to close the modal. Fill in all fields or delete the Restricted Performance being filled.'
                                                        )}
                                                    </Tooltip>
                                                }
                                            >
                                                <InformationIcon className="far fa-info-circle" />
                                            </OverlayTrigger>
                                        )
                                    )}
                                </div>
                                <i
                                    className={'far fa-chevron-' + (isModalDoubleSize ? 'left' : 'right')}
                                    style={{ color: '#37A3EF' }}
                                ></i>
                            </RestrictedPerformanceContainerSection>
                        </RestrictedPerformanceContainer>
                        <ActivityFooter
                            closeModal={closeModal}
                            event={activityEvent}
                            rawTubularList={rawTubularList}
                            tubularIndex={tubularIndex}
                            updateTubularIndex={updateTubularIndex}
                            validateFields={validateFields}
                            runEvent={runEvent}
                            updateTubularList={(event, previousButtonClicked) => {
                                updateTubularList(event, tubularIndex, previousButtonClicked)
                            }}
                            postActivities={() =>
                                isSingleEvent ? postActivity([rawActivityEvent]) : postActivity(rawTubularList)
                            }
                            isStartButtonDisabled={isAddRestrictedButtonDisabled}
                            isModalDoubleSize={isModalDoubleSize}
                            scrollToTop={scrollToTop}
                            isDeleteConfirmationModalOpen={isDeleteConfirmationModalOpen}
                            openDeleteConfirmationModal={openDeleteConfirmationModal}
                            closeDeleteConfirmationModal={closeDeleteConfirmationModal}
                            handleDelete={handleDelete}
                            hasChanged={hasChanged}
                        ></ActivityFooter>
                    </ModalFooter>
                </ActivityModalContainer>
            )}
        </>
    )
}

export const RestrictedSection = styled.div`
    padding-bottom: 15px;
    border-bottom: 1px solid var(--gray-300);
`

export const ModalLineContainer = styled.div`
    display: flex;
    gap: 17px;
`

const ActivityModalContainer = styled.div`
    height: calc(100% - 142px);
    display: flex;
    flex-flow: column;
    justify-content: space-between;
`

export const ScrollModalContainer = styled.div`
    display: flex;
    height: 100%;
`

const RestrictedPerformanceContainer = styled.div<{ isDisabled: boolean; liveTheme?: string }>`
    height: 64px;
    padding: 0 22px 0 20px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    border-top: 1px solid var(--gray-300);
    border-bottom: 1px solid var(--gray-300);
    cursor: ${({ isDisabled }) => (isDisabled ? 'not-allowed' : 'pointer')};

    background-color: ${({ isDisabled, liveTheme }) =>
        liveTheme === 'dark'
            ? isDisabled
                ? 'var(--gray-300)'
                : 'var(--gray-200)'
            : isDisabled
            ? 'var(--gray-100)'
            : 'white'};

    color: ${({ isDisabled }) => (isDisabled ? 'var(--gray-600)' : 'var(--gray-800)')};
`

const RestrictedPerformanceContainerSection = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`
